import React from 'react';
import { ArrowLeft, Home, LayoutGrid, FileText, Building, Wrench, FileCheck, ClipboardCheck, Award, Layers, Building2 } from 'lucide-react';
import type { CurrentPage } from '../App';

interface EstateAgencyPageProps {
  onNavigate: (page: CurrentPage) => void;
}

const EstateAgencyPage: React.FC<EstateAgencyPageProps> = ({ onNavigate }) => {
  // Scroll to top when page loads (backup - App.tsx also handles this)
  React.useLayoutEffect(() => {
    // Scroll the actual scrolling container (#root)
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.scrollTop = 0;
    }
    
    // Also try window (for compatibility)
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    console.log('🔝 [EstateAgencyPage] Scroll reset in useLayoutEffect, #root scrollTop:', rootElement?.scrollTop);
  }, []);

  const subcategories = [
    { name: 'Property Inventory', icon: FileText, targetPage: 'propertyInventory' as CurrentPage['page'] },
    { name: 'Floor Plan Creator', icon: Layers, targetPage: 'retrofit/floorplan' as CurrentPage['page'] },
  ];

  const handleSubcategoryClick = (targetPage: CurrentPage['page']) => {
    console.log(`Navigating to subcategory: ${targetPage}`);
    onNavigate({ page: targetPage } as CurrentPage);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50 pb-20">
      {/* Modern Header with Gradient */}
      <header className="bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 shadow-2xl sticky top-0 z-40 flex-shrink-0">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/0 via-white/10 to-indigo-600/0"></div>
        <div className="relative w-full px-4 sm:px-6 flex items-center justify-between app-header-padding">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white">
                Estate Agency Hub
              </h1>
              <p className="text-blue-100 text-sm hidden sm:block">Property management tools</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={() => onNavigate({ page: 'start' })}
              title="Go to Home"
              className="p-2.5 rounded-xl text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200 backdrop-blur-sm border border-white/20 hover:border-white/40 shadow-lg hover:shadow-xl"
            >
              <Home size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto p-4 sm:p-6 lg:p-8">
        {/* Welcome Section */}
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl mt-8 border border-slate-200/50 overflow-hidden">
          <div className="relative bg-gradient-to-r from-slate-50 to-blue-50/30 px-6 py-5 border-b border-slate-200/50">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
            <div className="relative">
              <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-slate-800 to-blue-700 bg-clip-text text-transparent mb-2">
                Property Management Tools
              </h2>
              <p className="text-slate-600 text-sm">Choose your preferred workflow for property documentation and management</p>
            </div>
          </div>
          
          <div className="p-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-3xl mx-auto">
              {subcategories.map((category, index) => (
                <button
                  key={category.name}
                  onClick={() => handleSubcategoryClick(category.targetPage)}
                  className="group relative bg-gradient-to-br from-white via-slate-50/50 to-blue-50/30 hover:from-blue-50/50 hover:via-white hover:to-indigo-50/40 transition-all duration-300 border-2 border-slate-200/60 hover:border-blue-300/60 rounded-2xl p-8 flex flex-col items-center justify-center text-center shadow-lg hover:shadow-2xl hover:shadow-blue-500/10 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:ring-offset-2 transform hover:scale-[1.02] hover:-translate-y-1"
                >
                  {/* Decorative background gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                  
                  <div className="relative z-10">
                    <div className="relative mb-6">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-2xl blur-xl transform scale-110 opacity-0 group-hover:opacity-100 transition-all duration-300"></div>
                      <div className="relative bg-gradient-to-br from-blue-500 to-indigo-600 p-4 rounded-2xl shadow-lg shadow-blue-500/25 group-hover:shadow-xl group-hover:shadow-blue-500/30 transition-all duration-300">
                        <category.icon className="w-10 h-10 text-white" strokeWidth={2} />
                      </div>
                    </div>
                    
                    <h3 className="text-lg font-bold text-slate-800 mb-3 group-hover:text-blue-700 transition-colors duration-200">
                      {category.name}
                    </h3>
                    
                    <p className="text-sm text-slate-600 leading-relaxed group-hover:text-slate-700 transition-colors duration-200">
                      {category.name === 'Property Inventory' && 'Document property features, fixtures, and condition reports for estate agency purposes'}
                      {category.name === 'Floor Plan Creator' && 'Create detailed floor plans with rooms, walls, windows, and heating elements'}
                    </p>
                    
                    {/* Progress indicator or badge */}
                    <div className="mt-4 inline-flex items-center space-x-1.5 px-3 py-1.5 bg-slate-100 group-hover:bg-blue-100 rounded-full transition-colors duration-200">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-xs font-medium text-slate-700 group-hover:text-blue-700">Ready</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

      </main>
    </div>
  );
};

export default EstateAgencyPage;
